/**
 * DomainStorageChart Component
 * Chart visualization for storage by domain
 */

import { forwardRef } from 'react';
import { Card, Stack, Text, Group } from '@/components/ui';
import { RingProgress } from '@mantine/core';
import { formatFileSize } from '../../utils/formatting';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface DomainStorageData {
  domain: string;
  fileCount: number;
  totalSize: number;
  percentage: number;
  color?: string;
}

export interface DomainStorageChartProps {
  /** Storage data by domain */
  data: DomainStorageData[];
  /** Chart type */
  chartType?: 'ring' | 'bar';
  /** Color scheme */
  colorScheme?: string[];
  /** Show legend */
  showLegend?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Chart component for visualizing storage by domain
 *
 * @example
 * ```tsx
 * <DomainStorageChart
 *   data={[
 *     { domain: 'User', fileCount: 100, totalSize: 10485760, percentage: 40 },
 *     { domain: 'Member', fileCount: 75, totalSize: 7864320, percentage: 30 },
 *     { domain: 'Event', fileCount: 50, totalSize: 5242880, percentage: 20 },
 *   ]}
 *   chartType="ring"
 *   showLegend
 * />
 * ```
 */
export const DomainStorageChart = forwardRef<HTMLDivElement, DomainStorageChartProps>(
  (props, ref) => {
    const {
      data,
      chartType = 'ring',
      colorScheme = [
        'var(--mantine-color-blue-5)',
        'var(--mantine-color-violet-5)',
        'var(--mantine-color-cyan-5)',
        'var(--mantine-color-green-5)',
        'var(--mantine-color-orange-5)',
        'var(--mantine-color-red-5)',
      ],
      showLegend = true,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Assign colors to data
    const dataWithColors = data.map((item, index) => ({
      ...item,
      color: item.color || colorScheme[index % colorScheme.length],
    }));

    if (chartType === 'ring') {
      return (
        <Card ref={ref} variant="elevated" className={className}>
          <Stack gap="lg" align="center">
            {/* Ring Progress */}
            <RingProgress
              size={200}
              thickness={20}
              sections={dataWithColors.map((item) => ({
                value: item.percentage,
                color: item.color,
                tooltip: `${item.domain}: ${item.percentage.toFixed(1)}%`,
              }))}
              label={
                <Stack gap={0} align="center">
                  <Text size="lg" fw={700} style={accessibilityStyles}>
                    {data.reduce((sum, item) => sum + item.fileCount, 0)}
                  </Text>
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    Total Files
                  </Text>
                </Stack>
              }
            />

            {/* Legend */}
            {showLegend && (
              <Stack gap="xs" style={{ width: '100%' }}>
                {dataWithColors.map((item) => (
                  <Group key={item.domain} justify="space-between">
                    <Group gap="xs">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: item.color,
                        }}
                      />
                      <Text size="sm" fw={500} style={accessibilityStyles}>
                        {item.domain}
                      </Text>
                    </Group>
                    <Group gap="md">
                      <Text size="sm" c="dimmed" style={accessibilityStyles}>
                        {item.fileCount} files
                      </Text>
                      <Text size="sm" fw={500} style={accessibilityStyles}>
                        {formatFileSize(item.totalSize)}
                      </Text>
                      <Text size="sm" c="dimmed" style={accessibilityStyles}>
                        ({item.percentage.toFixed(1)}%)
                      </Text>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      );
    }

    // Bar chart (simple implementation with CSS)
    return (
      <Card ref={ref} variant="elevated" className={className}>
        <Stack gap="md">
          {dataWithColors.map((item) => (
            <Stack key={item.domain} gap={4}>
              <Group justify="space-between">
                <Text size="sm" fw={500} style={accessibilityStyles}>
                  {item.domain}
                </Text>
                <Group gap="xs">
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>
                    {formatFileSize(item.totalSize)}
                  </Text>
                  <Text size="sm" fw={600} style={accessibilityStyles}>
                    {item.percentage.toFixed(1)}%
                  </Text>
                </Group>
              </Group>
              <div
                style={{
                  width: '100%',
                  height: 8,
                  backgroundColor: 'var(--mantine-color-gray-2)',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${item.percentage}%`,
                    height: '100%',
                    backgroundColor: item.color,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </Stack>
          ))}
        </Stack>
      </Card>
    );
  }
);

DomainStorageChart.displayName = 'DomainStorageChart';

export default DomainStorageChart;
